import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useMediaQuery from '../useMediaQuery';
import logo from '../Vector Vault Icon.png';

const inputStyle = {
  width: '100%',
  padding: '12px 14px',
  backgroundColor: '#111111',
  border: '1px solid #222222',
  borderRadius: '8px',
  color: '#ffffff',
  fontSize: '15px',
  fontFamily: "'Inter', sans-serif",
  outline: 'none',
  transition: 'border-color 0.2s',
  boxSizing: 'border-box',
};

const labelStyle = {
  display: 'block',
  marginBottom: '8px',
  fontSize: '14px',
  fontWeight: '500',
  color: '#999999',
};

const RegistrationForm = ({ form, handleChange, handleSubmit: originalHandleSubmit, loading, error }) => {
  const [confirmPassword, setConfirmPassword] = useState('');
  const isMobile = useMediaQuery({ maxWidth: 768 * 2 });
  const navigate = useNavigate();

  // Wrap the original handleSubmit to include redirection after form submission
  const handleSubmitAndRedirect = (event) => {
    event.preventDefault();
    // Add check for password match before calling the original handleSubmit
    if (form.password !== confirmPassword) {
      alert("Passwords do not match.");
      return; // Prevent form submission if passwords don't match
    }
    
    // Create a copy of the form with lowercased email
    const formWithLowercasedEmail = {
      ...form,
      email: form.email.toLowerCase()
    };
    
    // Create a modified event with the lowercased email form
    const modifiedEvent = {
      ...event,
      preventDefault: event.preventDefault.bind(event),
      target: {
        ...event.target,
        elements: {
          ...event.target.elements,
          email: { value: formWithLowercasedEmail.email }
        }
      }
    };
    
    // Call original handleSubmit with lowercased email data
    originalHandleSubmit(modifiedEvent, formWithLowercasedEmail);

    // Prepare query parameters for Stripe
    const queryParams = new URLSearchParams({
      'prefilled_email': formWithLowercasedEmail.email,
      'client_reference_id': `${form.first}_${form.last}`,
      'prefilled_first_name': form.first,
      'prefilled_last_name': form.last
    }).toString();

    // Redirect to the billing page after successful registration
    window.location.href = `https://billing.vectorvault.io/b/dR68A68fP29n4Ja5kq?${queryParams}`;
  };

  const handleLoginClick = () => {
    navigate('/login');
  };

  // Update to handle changes for both form fields and confirmation password
  const handleInputChange = (event) => {
    if (event.target.name === 'confirmPassword') {
      setConfirmPassword(event.target.value);
    } else {
      handleChange(event);
    }
  };

  // Style for the confirm password input when passwords match
  const confirmPasswordStyle = form.password && confirmPassword === form.password ? {
    backgroundImage: "url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2224%22 height=%2224%22 viewBox=%220 0 24 24%22><path fill=%22%2300d67a%22 d=%22M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z%22/></svg>')",
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 10px center',
    paddingRight: '30px'
  } : {};

  return (
    <div style={{ 
      backgroundColor: '#080808', 
      minHeight: '100vh', 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      padding: '40px 20px',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      color: '#ffffff',
    }}>
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        marginBottom: '36px',
        marginTop: isMobile ? '10px' : '24px',
      }}>
        <img
          src={logo}
          alt="logo"
          style={{
            height: isMobile ? '3.5rem' : '4rem',
            marginRight: '12px',
          }}
        />
        <span style={{ 
          fontSize: isMobile ? '1.8rem' : '2.2rem', 
          fontWeight: '700', 
          letterSpacing: '-0.03em',
          color: '#ffffff',
        }}>
          Vector Vault
        </span>
      </div>

      <div style={{ 
        width: '100%', 
        maxWidth: '420px',
        backgroundColor: '#0d0d0d',
        border: '1px solid #1a1a1a',
        borderRadius: '16px',
        padding: '36px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
      }}>
        <h2 style={{ 
          textAlign: 'center', 
          marginBottom: '28px', 
          fontSize: '1.5rem',
          fontWeight: '700',
          letterSpacing: '-0.02em',
          color: '#ffffff',
        }}>Register</h2>
        
        <form onSubmit={handleSubmitAndRedirect}>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>First name</label>
            <input 
              type="text" 
              name="first" 
              value={form.first} 
              onChange={handleChange}
              style={inputStyle}
              onFocus={(e) => e.target.style.borderColor = '#00d67a'}
              onBlur={(e) => e.target.style.borderColor = '#222222'}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Last name</label>
            <input 
              type="text" 
              name="last" 
              value={form.last} 
              onChange={handleChange}
              style={inputStyle}
              onFocus={(e) => e.target.style.borderColor = '#00d67a'}
              onBlur={(e) => e.target.style.borderColor = '#222222'}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Email</label>
            <input 
              type="email" 
              name="email" 
              value={form.email} 
              onChange={handleChange}
              style={inputStyle}
              onFocus={(e) => e.target.style.borderColor = '#00d67a'}
              onBlur={(e) => e.target.style.borderColor = '#222222'}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Password</label>
            <input 
              type="password" 
              name="password" 
              value={form.password} 
              onChange={handleInputChange}
              style={{ ...inputStyle, ...confirmPasswordStyle }}
              onFocus={(e) => e.target.style.borderColor = '#00d67a'}
              onBlur={(e) => e.target.style.borderColor = '#222222'}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>Confirm Password</label>
            <input 
              type="password" 
              name="confirmPassword" 
              value={confirmPassword} 
              onChange={handleInputChange}
              style={{ ...inputStyle, ...confirmPasswordStyle }}
              onFocus={(e) => e.target.style.borderColor = '#00d67a'}
              onBlur={(e) => e.target.style.borderColor = '#222222'}
            />
          </div>

          {form.password && confirmPassword && form.password !== confirmPassword && (
            <div style={{ color: '#ff6b6b', fontSize: '13px', marginBottom: '12px' }}>
              Passwords do not match.
            </div>
          )}

          <button 
            type="submit" 
            disabled={loading}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: '#00d67a',
              color: '#000000',
              border: 'none',
              borderRadius: '8px',
              fontSize: '15px',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'background-color 0.2s',
              fontFamily: "'Inter', sans-serif",
              opacity: loading ? 0.7 : 1,
            }}
            onMouseOver={(e) => !loading && (e.target.style.backgroundColor = '#00b368')}
            onMouseOut={(e) => !loading && (e.target.style.backgroundColor = '#00d67a')}
          >
            {loading ? 'Registering...' : 'Register'}
          </button>

          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <button
              type="button"
              onClick={handleLoginClick}
              style={{
                background: 'none',
                border: 'none',
                color: '#00d67a',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                padding: 0,
                fontFamily: "'Inter', sans-serif",
              }}
            >
              Already have an account? Login
            </button>
          </div>
        </form>

        {loading && (
          <div style={{ 
            marginTop: '16px', 
            padding: '10px 14px', 
            backgroundColor: 'rgba(0, 214, 122, 0.1)', 
            border: '1px solid rgba(0, 214, 122, 0.2)',
            borderRadius: '8px',
            color: '#00d67a',
            fontSize: '14px',
          }}>
            Loading...
          </div>
        )}
        {error && (
          <div style={{ 
            marginTop: '16px', 
            padding: '10px 14px', 
            backgroundColor: 'rgba(255, 68, 68, 0.1)', 
            border: '1px solid rgba(255, 68, 68, 0.2)',
            borderRadius: '8px',
            color: '#ff6b6b',
            fontSize: '14px',
          }}>
            An error occurred: {error}
          </div>
        )}
      </div>
    </div>
  );
};

export default RegistrationForm;
